<template>
  <section class="hero">
    <!-- Left: title & stats -->
    <div class="hero-left">
      <div v-if="stats?.lastRefreshDate" class="hero-kicker">
        <div class="hero-kicker-dot" />
        LIVE · MIS À JOUR <ClientOnly>{{ lastRefreshRelative }}</ClientOnly>
      </div>

      <h1 class="hero-title">
        Toutes les<br />
        <em>Compétitions</em><br />
        <span class="outline">CrossFit</span> en<br />
        France.
      </h1>

      <p class="hero-desc">
        Le scanner indépendant des événements fitness français. Throwdowns,
        Hyrox, qualifiers en ligne, challenges régionaux, tout agrégé depuis
        Scoring Fit, Competition Corner &amp; les boxes.
      </p>

      <p class="hero-desc-mobile">
        Scanner indépendant. Throwdowns, Hyrox, qualifiers en ligne.
      </p>

      <div class="hero-stats">
        <div>
          <div class="hero-stat-value">{{ totalCount }}</div>
          <div class="hero-stat-label">Événements</div>
        </div>
        <div>
          <div class="hero-stat-value">{{ onsiteCount }}</div>
          <div class="hero-stat-label">Sur place</div>
        </div>
        <div>
          <div class="hero-stat-value">{{ onlineCount }}</div>
          <div class="hero-stat-label">En ligne</div>
        </div>
      </div>

      <button class="hero-week-btn" @click="seeWeekEvents">
        Voir les événements de cette semaine →
      </button>
    </div>

    <!-- Right: upcoming weekend events -->
    <div class="hero-right">
      <div class="hero-right-header">
        <span class="hero-right-title">CE WEEKEND / {{ weekendLabel }}</span>
      </div>

      <div class="wc-scroll">
        <template v-if="weekendEvents.length">
          <div
            v-for="event in weekendEvents"
            :key="event._id"
            class="weekend-card"
            role="button"
            tabindex="0"
            @click="openDetailPanel(event)"
            @keydown.enter="openDetailPanel(event)"
          >
            <div class="wc-card-top">
              <div class="wc-date">
                <span class="d">{{ formatDay(event.startDate) }}</span>
                <span class="m">{{ formatMonth(event.startDate) }}</span>
              </div>
            </div>
            <div class="wc-info">
              <div class="wc-name">{{ event.name }}</div>
              <div class="wc-meta">
                {{ event.city
                }}{{
                  event.departementCode ? ` (${event.departementCode})` : ''
                }}
                · {{ event.duration }}J
              </div>
            </div>
            <span class="wc-voir">VOIR →</span>
          </div>
          <button
            v-if="remainingCount > 0"
            class="wc-more"
            @click="seeWeekendEvents"
          >
            + {{ remainingCount }} autres événements →
          </button>
        </template>

        <div
          v-if="!weekendEvents.length"
          style="
            color: var(--ink-dim);
            font-family: var(--mono);
            font-size: 10px;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            padding-top: 16px;
          "
        >
          Aucun événement ce weekend
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import type { EventItem } from '@/types/Event';
import { API_ENDPOINTS } from '@/config/api'; // used for EVENTS (weekend fetch)

interface FetchEventResponse {
  results: EventItem[];
  count: number;
}

const { fetchWithAuth } = useApiConfig();
const { open: openDetailPanel } = useEventDetail();
const { setFilters } = useFilters();
const { data: stats, lastRefreshRelative } = useStats();

const totalCount = computed(() => stats.value?.total ?? '—');
const onsiteCount = computed(() => stats.value?.onSite ?? '—');
const onlineCount = computed(() => stats.value?.online ?? '—');

const weekendRange = useState('weekend-range', () => {
  const today = dayjs();
  const dayOfWeek = today.day();
  const saturday = dayOfWeek === 6 ? today : today.add(6 - dayOfWeek, 'day');
  const sunday = saturday.add(1, 'day');
  return {
    start: saturday.format('YYYY-MM-DD'),
    end: sunday.format('YYYY-MM-DD'),
    saturdayDay: saturday.format('D'),
    sundayDay: sunday.format('D'),
    month: saturday.format('MMMM').toUpperCase(),
  };
});

const weekendLabel = computed(() => {
  const r = weekendRange.value;
  return `${r.saturdayDay}–${r.sundayDay} ${r.month}`;
});

const { data: weekendData } = useAsyncData<FetchEventResponse>(
  'weekend-events',
  () =>
    fetchWithAuth<FetchEventResponse>(API_ENDPOINTS.EVENTS, {
      query: {
        startDate: weekendRange.value.start,
        endDate: weekendRange.value.end,
        type: 'onSite',
        size: 5,
        page: 1,
      },
    }),
  { default: () => ({ results: [], count: 0 }) },
);

const weekendEvents = computed(() => weekendData.value?.results ?? []);
const weekendCount = computed(() => weekendData.value?.count ?? 0);
const remainingCount = computed(() =>
  Math.max(0, weekendCount.value - weekendEvents.value.length),
);

const weekRange = computed(() => {
  const today = dayjs();
  const dayOfWeek = today.day();
  const monday =
    dayOfWeek === 0
      ? today.subtract(6, 'day')
      : today.subtract(dayOfWeek - 1, 'day');
  const sunday = monday.add(6, 'day');
  return { start: monday.toDate(), end: sunday.toDate() };
});

function scrollToEvents() {
  setTimeout(() => {
    document
      .querySelector('.filter-bar')
      ?.scrollIntoView({ behavior: 'smooth' });
  }, 600);
}

function seeWeekendEvents() {
  setFilters({
    startDate: dayjs(weekendRange.value.start).toDate(),
    endDate: dayjs(weekendRange.value.end).toDate(),
  });
  scrollToEvents();
}

function seeWeekEvents() {
  setFilters({
    startDate: weekRange.value.start,
    endDate: weekRange.value.end,
  });
  scrollToEvents();
}

const formatDay = (date: Date | string) => dayjs.utc(date).format('D');
const formatMonth = (date: Date | string) =>
  dayjs.utc(date).format('MMM').toUpperCase().replace('.', '');
</script>
